import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CATEGORIES } from "../constants";
import ListingCard from "../components/ListingCard";
import VerifiedBadge from "../components/VerifiedBadge";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Repeat, Euro, MapPin, Calendar, Eye, Star, ChevronRight, Heart, Share2, Shield, MessageCircle } from "lucide-react";
import OfferModal from "../components/OfferModal";
import BarterModal from "../components/BarterModal";
import ShareListingBlock from "../components/ShareListingBlock";
import { listingsAPI, resolveImg, chatAPI } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [offerOpen, setOfferOpen] = useState(false);
  const [barterOpen, setBarterOpen] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    listingsAPI.get(id).then((l) => {
      setListing(l);
      listingsAPI.list({ category: l.category, limit: 5 }).then(list => setSimilar(list.filter(x => x.id !== l.id).slice(0, 4)));
    }).catch(() => setListing(null));
  }, [id]);

  if (listing === null) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <p className="text-slate-500">Caricamento...</p>
      </div>
    );
  }

  const seller = listing.user;
  const category = CATEGORIES.find(c => c.id === listing.category);

  const requireAuth = (action) => {
    if (!user) {
      toast.info("Accedi per continuare");
      navigate("/login");
      return false;
    }
    return true;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <nav className="flex items-center gap-1 text-xs text-slate-500 mb-4">
        <Link to="/" className="hover:text-[#e86a24]">Home</Link>
        <ChevronRight className="w-3 h-3"/>
        <Link to={`/categoria/${category?.id}`} className="hover:text-[#e86a24]">{category?.name}</Link>
        <ChevronRight className="w-3 h-3"/>
        <span className="text-slate-900 font-medium line-clamp-1">{listing.title}</span>
      </nav>

      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        <div>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="relative aspect-[4/3] bg-slate-100">
              <img
                src={resolveImg(listing.images?.[imgIdx])}
                alt={listing.title}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'><rect width='400' height='300' fill='%23f1f5f9'/><g fill='%2394a3b8' font-family='system-ui' font-size='14' text-anchor='middle'><text x='200' y='150'>Foto non disponibile</text><text x='200' y='170' font-size='10'>L'immagine non è più reperibile</text></g></svg>";
                }}
              />
              {listing.featured && (
                <Badge className="absolute top-3 left-3 bg-amber-500 hover:bg-amber-500 text-white border-0 font-bold">
                  <Star className="w-3 h-3 mr-1 fill-white"/> VETRINA
                </Badge>
              )}
              <div className="absolute top-3 right-3 flex gap-2">
                <button className="w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white shadow" aria-label="Preferiti">
                  <Heart className="w-4 h-4"/>
                </button>
                <button className="w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white shadow" aria-label="Condividi">
                  <Share2 className="w-4 h-4"/>
                </button>
              </div>
            </div>
            {(listing.images?.length || 0) > 1 && (
              <div className="flex gap-2 p-3 overflow-x-auto">
                {listing.images.map((img, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${i===imgIdx ? "border-[#e86a24]" : "border-transparent"} bg-slate-100`}>
                    <img
                      src={resolveImg(img)}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'><rect width='80' height='80' fill='%23f1f5f9'/><g fill='%2394a3b8'><circle cx='40' cy='34' r='10' fill='none' stroke='%2394a3b8' stroke-width='2'/><path d='M20 65 L35 50 L50 60 L65 45' stroke='%2394a3b8' stroke-width='2' fill='none'/><line x1='25' y1='25' x2='55' y2='55' stroke='%23ef4444' stroke-width='3'/></g></svg>";
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-4">
            {listing.shipping_from_italy && (
              <div className="mb-4 rounded-xl bg-gradient-to-r from-green-700 to-green-600 text-white p-4 flex items-start gap-3" data-testid="banner-shipping-italy">
                <div className="text-3xl shrink-0">🇮🇹</div>
                <div className="flex-1">
                  <div className="font-black text-base uppercase tracking-wide">Spedito da Italia</div>
                  <div className="text-sm text-green-50 mt-0.5">
                    Il prezzo include il <strong>costo di spedizione</strong>. Venditore: azienda registrata in Italia.
                  </div>
                </div>
              </div>
            )}
            <h1 className="text-2xl font-black text-[#1d3d3a]">{listing.title}</h1>
            <div className="mt-2 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{listing.castello}</span>
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5"/>Pubblicato {new Date(listing.created_at).toLocaleDateString("it-IT")}</span>
              <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5"/>{listing.views} visualizzazioni</span>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <Badge variant="secondary">{listing.condition}</Badge>
              <Badge variant="secondary">{category?.name}</Badge>
              {listing.allow_barter && <Badge className="bg-emerald-600 hover:bg-emerald-600">Baratto accettato</Badge>}
              {listing.allow_offer && <Badge className="bg-[#1d3d3a] hover:bg-[#1d3d3a]">Offerte accettate</Badge>}
            </div>

            {listing.category === "auto" && listing.auto_details && (
              <div className="mt-6 border-t border-slate-200 pt-5">
                <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="inline-flex w-7 h-7 rounded-lg bg-sky-100 text-sky-700 items-center justify-center text-xs font-black">🚗</span>
                  Dettagli vettura
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {listing.auto_details.brand && <SpecCell label="Marca" value={listing.auto_details.brand} />}
                  {listing.auto_details.model && <SpecCell label="Modello" value={listing.auto_details.model} />}
                  {listing.auto_details.year && <SpecCell label="Anno" value={listing.auto_details.year} />}
                  {listing.auto_details.km && <SpecCell label="Km" value={`${Number(listing.auto_details.km).toLocaleString("it-IT")} km`} />}
                </div>
                {listing.auto_details.features && (
                  <div className="mt-3 bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm text-slate-700">
                    <div className="text-xs font-bold uppercase tracking-wider text-sky-700 mb-1">Caratteristiche</div>
                    <p className="whitespace-pre-line">{listing.auto_details.features}</p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-6 border-t border-slate-200 pt-5">
              <h3 className="font-bold text-slate-900 mb-2">Descrizione</h3>
              <p className="text-slate-700 whitespace-pre-line leading-relaxed">{listing.description}</p>
            </div>
          </div>

          {similar.length > 0 && (
            <div className="mt-8">
              <h3 className="font-black text-xl text-[#1d3d3a] mb-4">Annunci simili</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {similar.map(l => <ListingCard key={l.id} listing={l} />)}
              </div>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 h-fit space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="text-4xl font-black text-[#e86a24]">€ {Number(listing.price).toLocaleString("it-IT")}</div>
            <div className="mt-4 space-y-2">
              <Button
                onClick={async () => {
                  if (!requireAuth()) return;
                  try {
                    const conv = await chatAPI.start(listing.id);
                    navigate(`/messaggi/${conv.id}`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Errore avvio chat");
                  }
                }}
                className="w-full bg-[#1d3d3a] hover:bg-[#102624] text-white h-12 rounded-xl font-semibold"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> Invia messaggio
              </Button>

              {listing.allow_offer && (
                <Button
                  onClick={() => requireAuth() && setOfferOpen(true)}
                  variant="outline"
                  className="w-full h-12 rounded-xl border-2 border-[#e86a24] text-[#e86a24] hover:bg-[#e86a24] hover:text-white font-semibold"
                >
                  <Euro className="w-4 h-4 mr-2" />
                  Fai un'offerta
                </Button>
              )}

              {listing.allow_barter && (
                <Button
                  onClick={() => requireAuth() && setBarterOpen(true)}
                  variant="outline"
                  className="w-full h-12 rounded-xl border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white font-semibold"
                >
                  <Repeat className="w-4 h-4 mr-2" />
                  Proponi baratto
                </Button>
              )}
            </div>

            <div className="mt-4 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
              <Shield className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <div>Pagamento protetto disponibile tramite Stripe. Incontro in sicurezza nei Castelli di SM.</div>
            </div>
          </div>

          {seller && (
            <Link to={`/u/${seller.id}`} className="block bg-white border border-slate-200 rounded-2xl p-6 hover:border-[#e86a24] hover:shadow-md transition-all" data-testid="seller-card-link">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Venditore</h3>
              <div className="flex items-center gap-3">
                <img src={seller.avatar} alt={seller.name} className="w-14 h-14 rounded-full" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <div className="font-bold text-slate-900 truncate">{seller.name}</div>
                    {seller.is_verified && <VerifiedBadge size="sm" />}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    <span className="font-semibold text-slate-700">{Number(seller.rating).toFixed(1)}</span>
                    <span>({seller.reviews_count} recensioni)</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{seller.castello}</div>
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#e86a24] shrink-0">Vedi profilo →</div>
              </div>
            </Link>
          )}

          <ShareListingBlock listing={listing} />
        </aside>
      </div>

      <OfferModal open={offerOpen} onClose={() => setOfferOpen(false)} listing={listing} />
      <BarterModal open={barterOpen} onClose={() => setBarterOpen(false)} listing={listing} />
    </div>
  );
}

function SpecCell({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-black text-[#1d3d3a] mt-0.5">{value}</div>
    </div>
  );
}

